import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, ChevronRight, Menu, Moon, Search, Sun } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useThemeStore } from '@/store/theme';
import { formatRelative } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

/**
 * Bilah atas.
 *
 * ── Kenapa latarnya menyatu dengan kanvas ────────────────────────────────
 *
 * Dulu bilah ini berlatar `--surface-raised` yang putih, sementara kanvas di
 * bawahnya abu dan kartunya putih lagi. Mata melihat tiga lapis warna yang
 * saling bersaing padahal isinya satu halaman. Menyamakan latarnya dengan
 * kanvas menghapus satu lapis, dan kartu jadi terlihat mengambang di satu
 * bidang utuh. Garis bawahnya ikut dilepas karena batasnya sudah tidak ada.
 *
 * ── Kenapa judul diganti jejak lokasi ────────────────────────────────────
 *
 * Judul halaman mengulang apa yang sudah ditandai sidebar. Lebih buruk lagi,
 * "Detail Job" tidak memberi tahu job yang mana. Jejak lokasi menyebutkan
 * induknya dan bisa ditekan untuk kembali, jadi ruang yang sama akhirnya
 * membawa sesuatu yang berguna.
 */
export const Topbar = ({ jejak, onMenuClick, onSearchClick }) => {
  const { isDark, toggle } = useThemeStore();

  return (
    <header className="sticky top-0 z-30 flex h-[52px] items-center gap-3 bg-[var(--surface-canvas)]/90 px-4 backdrop-blur-md lg:px-6">
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        onClick={onMenuClick}
        aria-label="Buka menu"
        className="lg:hidden"
      >
        <Menu className="size-5" />
      </Button>

      <Jejak bagian={jejak} />

      <div className="ml-auto flex items-center gap-1.5">
        {/* Pemicu palet perintah. Di layar sempit hanya ikon; di layar lebar
            ia menyerupai kolom pencarian lengkap dengan petunjuk pintasan. */}
        <button
          type="button"
          onClick={onSearchClick}
          className={cn(
            // Lebarnya dipatok kecil dan tidak ikut memanjang. Ini pemicu
            // palet perintah, bukan kolom isian: melebarkannya cuma membuat
            // bilah atas terlihat penuh oleh sesuatu yang tidak diketik di
            // sana. Yang mengetik pun hampir selalu memakai Ctrl K.
            'hidden items-center gap-2 rounded-lg border border-[var(--border-default)] px-2.5 py-1.5 sm:flex',
            'bg-[var(--surface-raised)] text-[13px] text-[var(--text-muted)] transition-colors',
            'hover:border-[var(--border-strong)] hover:bg-[var(--surface-sunken)]',
          )}
        >
          <Search className="size-[15px] shrink-0" />
          <span>Cari</span>
          <kbd className="shrink-0 rounded border border-[var(--border-default)] bg-[var(--surface-sunken)] px-1.5 py-0.5 font-sans text-[10px] font-semibold">
            Ctrl K
          </kbd>
        </button>

        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onSearchClick}
          aria-label="Cari"
          className="sm:hidden"
        >
          <Search className="size-5" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={toggle}
          aria-label={isDark ? 'Ganti ke mode terang' : 'Ganti ke mode gelap'}
        >
          {isDark ? <Sun className="size-5" /> : <Moon className="size-5" />}
        </Button>

        <NotificationBell />
      </div>
    </header>
  );
};

/**
 * Jejak lokasi.
 *
 * Menerima satu atau dua bagian. Satu bagian berarti halaman tingkat atas dan
 * ditampilkan apa adanya; dua bagian berarti halaman dalam, dan yang pertama
 * bisa ditekan untuk kembali ke induknya.
 *
 * Beratnya sengaja lebih ringan daripada judul yang dulu (`text-lg font-bold`).
 * Tulisan terbesar di layar sebaiknya milik isi halaman, bukan milik label
 * yang menyebutkan halaman apa yang sedang dibuka.
 */
const Jejak = ({ bagian }) => {
  const daftar = Array.isArray(bagian) ? bagian.filter(Boolean) : [];
  if (daftar.length === 0) return null;

  const kini = daftar[daftar.length - 1];
  const induk = daftar.length > 1 ? daftar[daftar.length - 2] : null;

  return (
    <nav aria-label="Lokasi Anda" className="flex min-w-0 items-center gap-1.5 text-sm">
      {induk && (
        <>
          <Link
            to={induk.to}
            className="shrink-0 font-medium text-[var(--text-muted)] transition-colors hover:text-accent-600 hover:underline dark:hover:text-accent-400"
          >
            {induk.label}
          </Link>
          <ChevronRight
            className="size-3.5 shrink-0 text-[var(--text-muted)]"
            aria-hidden="true"
          />
        </>
      )}
      <span className="truncate font-bold text-[var(--text-primary)]" aria-current="page">
        {kini.label}
      </span>
    </nav>
  );
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get('/notifications', { params: { limit: 8 } }),
    // Notifikasi datang dari worker yang berjalan di latar; polling ringan
    // memastikan lonceng tetap akurat meski koneksi realtime terputus.
    refetchInterval: 60_000,
    retry: false,
  });

  const notifications = data?.data ?? [];
  const unread = notifications.filter((n) => !n.readAt).length;

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (event) => {
      if (!panelRef.current?.contains(event.target)) setOpen(false);
    };
    const onEscape = (event) => event.key === 'Escape' && setOpen(false);

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const markAllRead = async () => {
    await api.post('/notifications/read-all').catch(() => {});
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `${unread} notifikasi belum dibaca` : 'Notifikasi'}
        aria-expanded={open}
        className="relative"
      >
        <Bell className="size-5" />
        {unread > 0 && (
          <span className="absolute top-1.5 right-1.5 grid min-w-[15px] place-items-center rounded-full bg-danger-500 px-1 text-[9px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 origin-top-right animate-in overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-overlay)] shadow-xl">
          <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-3">
            <p className="text-sm font-bold text-[var(--text-primary)]">Notifikasi</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="inline-flex items-center gap-1 text-xs font-semibold text-accent-600 hover:underline dark:text-accent-400"
              >
                <Check className="size-3.5" />
                Tandai dibaca
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">
                Belum ada notifikasi.
              </p>
            ) : (
              notifications.map((n) => (
                <Link
                  key={n.id}
                  to={n.link ?? '#'}
                  onClick={() => setOpen(false)}
                  className={cn(
                    'block border-b border-[var(--border-subtle)] px-4 py-3 last:border-0',
                    'transition-colors hover:bg-[var(--surface-sunken)]',
                    !n.readAt && 'bg-accent-50/50 dark:bg-accent-500/5',
                  )}
                >
                  <div className="flex items-start gap-2.5">
                    <span
                      className={cn(
                        'mt-1.5 size-1.5 shrink-0 rounded-full',
                        n.severity === 'error'
                          ? 'bg-danger-500'
                          : n.severity === 'success'
                            ? 'bg-success-500'
                            : 'bg-accent-500',
                      )}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[var(--text-primary)]">{n.title}</p>
                      {n.body && (
                        <p className="mt-0.5 text-xs leading-relaxed text-[var(--text-muted)]">
                          {n.body}
                        </p>
                      )}
                      <p className="mt-1 text-[11px] text-[var(--text-muted)]">
                        {formatRelative(n.createdAt)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
